/**
 * Apartment documents (COL — owner-portal item 6): metadata rows here, file
 * bytes in the `contracts` storage bucket (same convention as
 * useOwnerDetail.ts's owner_documents helpers). source='admin' rows are
 * staff-uploaded and view-only for the owner; source='owner' rows are
 * uploaded (and only deletable) by the owning account.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const DOC_BUCKET = "contracts";

export type DocumentScope = "property" | "apartment" | "room" | "tenant";

export type ApartmentDocument = {
  id: string;
  /** Which level this row belongs to; apartment_id/room_id follow from it. */
  scope: DocumentScope;
  apartment_id: number | null;
  room_id: number | null;
  /** Set only on scope='tenant' rows — the tenancy the document is filed
   *  against. NOT the tenant's own documents, which live in tenant_documents
   *  and are theirs; these are filed BY the owner or by staff. */
  booking_id: number | null;
  property_id: number;
  /** Uploader's note. Editable by the uploader; staff may edit any. */
  message: string | null;
  title: string;
  file_url: string;
  file_name: string | null;
  file_size: number | null;
  content_type: string | null;
  source: "admin" | "owner";
  uploaded_by: string | null;
  /**
   * Display name of whoever uploaded this, resolved from `profiles` — their
   * name, or their email when they never set one.
   *
   * Every query in this file fills it in. "From owner" told a reader which
   * SIDE a file came from, which is the one thing they could already infer
   * from where they were standing — a property with three co-owners showed
   * three people's uploads under one anonymous badge. null only when the row
   * has no uploader at all (legacy rows) or the profile carries neither a name
   * nor an address.
   */
  uploader_name: string | null;
  created_at: string;
  deleted_at: string | null;
  delete_reason: string | null;
};

/**
 * Attach each row's uploader name, resolved in one lookup.
 *
 * `profiles` is world-readable (its SELECT policy is USING (true)), so this is
 * a second query rather than an RPC or a join the API cannot express. Rows come
 * back unchanged apart from the added name — a failed lookup degrades to null
 * names, never to a thrown query: a document list is still useful without the
 * attribution, and useless if it refuses to load.
 *
 * Exported because the Activity feed's document-history query needs the same
 * names over a different row set (soft-deleted rows included).
 */
export async function withUploaderNames(
  rows: Array<Omit<ApartmentDocument, "uploader_name">>
): Promise<ApartmentDocument[]> {
  const uploaderIds = Array.from(
    new Set(rows.map((r) => r.uploaded_by).filter(Boolean))
  ) as string[];
  if (uploaderIds.length === 0) {
    return rows.map((r) => ({ ...r, uploader_name: null }));
  }
  const { data: people } = await supabase
    .from("profiles")
    .select("id, name, email")
    .in("id", uploaderIds);
  const nameById = new Map<string, string | null>(
    (
      (people ?? []) as Array<{
        id: string;
        name: string | null;
        email: string | null;
      }>
    ).map((p) => [
      p.id,
      // Email is the fallback, not a second field. `name` is optional on
      // profiles and plenty of owner accounts never set one — without this the
      // badge degraded to the anonymous "From owner" for exactly those people,
      // which is the case the name was added for. An address identifies a
      // person to staff; a blank does not.
      p.name?.trim() || p.email?.trim() || null,
    ])
  );
  return rows.map((r) => ({
    ...r,
    uploader_name: r.uploaded_by ? (nameById.get(r.uploaded_by) ?? null) : null,
  }));
}

// `apartment_documents.file_url` is a bare path in the contracts bucket.
// Readers sign it on click (`openStoredFile` / `<StoredFileLink>`); there is
// no public URL to build any more (COL-436).

export function useApartmentDocuments(apartmentId: number | null) {
  return useQuery({
    queryKey: ["v2", "apartment-documents", apartmentId],
    enabled: !!apartmentId,
    queryFn: async (): Promise<ApartmentDocument[]> => {
      const { data, error } = await supabase
        .from("apartment_documents")
        .select("*")
        .eq("scope", "apartment")
        .eq("apartment_id", apartmentId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return withUploaderNames(
        (data ?? []) as Array<Omit<ApartmentDocument, "uploader_name">>
      );
    },
  });
}

/**
 * Documents for a whole property. `scopes` defaults to apartment-only, which
 * is what this hook meant before property- and room-level rows existed — an
 * unfiltered property_id query would now quietly fold all three levels into
 * every caller that used to get apartments alone.
 */
export function usePropertyApartmentDocuments(
  propertyId: number | null,
  scopes: DocumentScope[] = ["apartment"]
) {
  return useQuery({
    queryKey: ["v2", "property-apartment-documents", propertyId, scopes],
    enabled: !!propertyId,
    queryFn: async (): Promise<ApartmentDocument[]> => {
      const { data, error } = await supabase
        .from("apartment_documents")
        .select("*")
        .eq("property_id", propertyId)
        .in("scope", scopes)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return withUploaderNames(
        (data ?? []) as Array<Omit<ApartmentDocument, "uploader_name">>
      );
    },
  });
}

/** Documents attached to the property itself (not to a unit or a room). */
export function usePropertyLevelDocuments(propertyId: number | null) {
  return useQuery({
    queryKey: ["v2", "property-level-documents", propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<ApartmentDocument[]> => {
      const { data, error } = await supabase
        .from("apartment_documents")
        .select("*")
        .eq("scope", "property")
        .eq("property_id", propertyId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return withUploaderNames(
        (data ?? []) as Array<Omit<ApartmentDocument, "uploader_name">>
      );
    },
  });
}

/** Documents attached to one room. */
export function useRoomDocuments(roomId: number | null) {
  return useQuery({
    queryKey: ["v2", "room-documents", roomId],
    enabled: !!roomId,
    queryFn: async (): Promise<ApartmentDocument[]> => {
      const { data, error } = await supabase
        .from("apartment_documents")
        .select("*")
        .eq("scope", "room")
        .eq("room_id", roomId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return withUploaderNames(
        (data ?? []) as Array<Omit<ApartmentDocument, "uploader_name">>
      );
    },
  });
}

/**
 * Documents filed AGAINST one tenancy (scope='tenant'), by the owner or by
 * staff — distinct from `tenant_documents`, which is the tenant's own store
 * (passport, payslips) and stays out of reach here.
 *
 * Uploader names come from `withUploaderNames` like every other level does —
 * this hook resolved them itself first, which is where that helper came from.
 */
export type FiledTenantDocument = ApartmentDocument;

export function useTenantFiledDocuments(bookingId: number | null) {
  return useQuery({
    queryKey: ["v2", "tenant-documents-filed", bookingId],
    enabled: !!bookingId,
    queryFn: async (): Promise<FiledTenantDocument[]> => {
      const { data, error } = await supabase
        .from("apartment_documents")
        .select("*")
        .eq("scope", "tenant")
        .eq("booking_id", bookingId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return withUploaderNames(
        (data ?? []) as Array<Omit<ApartmentDocument, "uploader_name">>
      );
    },
  });
}

/** Rooms (id + title) for a property the caller owns — the room-level upload
 *  picker, for the same reason useOwnerPropertyApartments exists. */
export function useOwnerPropertyRooms(propertyId: number | null) {
  return useQuery({
    ...ownerPropertyRoomsQuery(propertyId),
    enabled: !!propertyId,
  });
}

/** The query behind useOwnerPropertyRooms, for callers that need it for
 *  several properties at once (useQueries). */
export function ownerPropertyRoomsQuery(propertyId: number | null) {
  return {
    queryKey: ["v2", "owner-property-rooms", propertyId],
    queryFn: async (): Promise<
      Array<{ id: number; title: string; apartment_id: number | null }>
    > => {
      const { data, error } = await supabase.rpc("owner_property_rooms", {
        _property_id: propertyId,
      });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: number;
        title: string;
        apartment_id: number | null;
      }>;
    },
  };
}

/** Apartments (id + label) for a property the caller owns — used by the owner
 *  Documents upload picker. apartments has no owner-facing SELECT policy. */
export function useOwnerPropertyApartments(propertyId: number | null) {
  return useQuery({
    ...ownerPropertyApartmentsQuery(propertyId),
    enabled: !!propertyId,
  });
}

/** The query behind useOwnerPropertyApartments, for useQueries callers. */
export function ownerPropertyApartmentsQuery(propertyId: number | null) {
  return {
    queryKey: ["v2", "owner-property-apartments", propertyId],
    queryFn: async (): Promise<Array<{ id: number; label: string }>> => {
      const { data, error } = await supabase.rpc("owner_property_apartments", {
        _property_id: propertyId,
      });
      if (error) throw error;
      return (data ?? []) as Array<{ id: number; label: string }>;
    },
  };
}

export type UploadDocumentParams = {
  propertyId: number;
  file: File;
  title?: string;
  message?: string;
  source: "admin" | "owner";
} & (
  | {
      scope: "property";
      apartmentId?: never;
      roomId?: never;
      bookingId?: never;
    }
  | {
      scope: "apartment";
      apartmentId: number;
      roomId?: never;
      bookingId?: never;
    }
  | { scope: "room"; roomId: number; apartmentId?: never; bookingId?: never }
  | { scope: "tenant"; bookingId: number; apartmentId?: never; roomId?: never }
);

/**
 * Upload at any level. The union above makes the target a type error rather
 * than a runtime CHECK violation: `scope: "room"` without a roomId will not
 * compile, which is where the DB constraint would otherwise catch it — after
 * the file had already been written to storage.
 */
export function useUploadApartmentDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: UploadDocumentParams): Promise<void> => {
      const safe = params.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const targetId =
        params.scope === "apartment"
          ? params.apartmentId
          : params.scope === "room"
            ? params.roomId
            : params.scope === "tenant"
              ? params.bookingId
              : params.propertyId;
      const path = `apartment-documents/${params.scope}/${targetId}/${Date.now()}-${safe}`;
      const up = await supabase.storage
        .from(DOC_BUCKET)
        .upload(path, params.file, { upsert: false });
      if (up.error) throw up.error;

      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase.from("apartment_documents").insert({
        scope: params.scope,
        // Exactly one target, matching apartment_documents_scope_target_check.
        apartment_id: params.scope === "apartment" ? params.apartmentId : null,
        room_id: params.scope === "room" ? params.roomId : null,
        booking_id: params.scope === "tenant" ? params.bookingId : null,
        property_id: params.propertyId,
        title: params.title?.trim() || params.file.name,
        message: params.message?.trim() || null,
        file_url: path,
        file_name: params.file.name,
        file_size: params.file.size,
        content_type: params.file.type || null,
        source: params.source,
        uploaded_by: auth?.user?.id ?? null,
      });
      if (error) {
        // Never leave the bytes behind when the row was rejected — an orphan
        // in the bucket is invisible to every screen but still counts.
        await supabase.storage.from(DOC_BUCKET).remove([path]);
        throw error;
      }
    },
    onSuccess: (_data, vars) => {
      invalidateDocumentQueries(qc, {
        propertyId: vars.propertyId,
        apartmentId: vars.scope === "apartment" ? vars.apartmentId : null,
        roomId: vars.scope === "room" ? vars.roomId : null,
        bookingId: vars.scope === "tenant" ? vars.bookingId : null,
      });
    },
  });
}

/**
 * One target the same file is filed against. `scope` decides which id matters,
 * exactly as in UploadDocumentParams — this is that union without the file.
 */
export type UploadTargetRef =
  | { scope: "property" }
  | { scope: "apartment"; apartmentId: number }
  | { scope: "room"; roomId: number }
  /* Tenancies are targets too, now that one upload control covers all four
     levels. The DB trigger enforce_apartment_document_target already checks
     that the booking's room sits in this property, so a mis-picked tenant is
     rejected server-side rather than filed against a stranger. */
  | { scope: "tenant"; bookingId: number };

export type UploadToTargetsParams = {
  propertyId: number;
  file: File;
  targets: UploadTargetRef[];
  title?: string;
  message?: string;
  source: "admin" | "owner";
};

/**
 * File ONE document against several levels at once — the whole property, some
 * of its apartments, some of its rooms, in any combination.
 *
 * The bytes are uploaded once and every row points at that same path. The rows
 * go in as a single multi-row insert, so the DB's one-target-per-row CHECK
 * (apartment_documents_scope_target_check) is satisfied per row without
 * relaxing it, and a rejected target takes the whole set with it rather than
 * leaving a document filed against two of the three places the owner picked.
 *
 * Consequence worth knowing at the call site: the file appears as a separate
 * card per target, and rename/delete acts on one of them. That is the trade
 * for not restructuring the table into target arrays, which every existing
 * document query and RLS policy is written against.
 */
export function useUploadDocumentToTargets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: UploadToTargetsParams): Promise<void> => {
      if (params.targets.length === 0) {
        throw new Error("Pick at least one place to file this document.");
      }
      const safe = params.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      // Path names the property, not a target: one file, many rows.
      const path = `apartment-documents/multi/${params.propertyId}/${Date.now()}-${safe}`;
      const up = await supabase.storage
        .from(DOC_BUCKET)
        .upload(path, params.file, { upsert: false });
      if (up.error) throw up.error;

      const { data: auth } = await supabase.auth.getUser();
      const shared = {
        property_id: params.propertyId,
        title: params.title?.trim() || params.file.name,
        message: params.message?.trim() || null,
        file_url: path,
        file_name: params.file.name,
        file_size: params.file.size,
        content_type: params.file.type || null,
        source: params.source,
        uploaded_by: auth?.user?.id ?? null,
      };
      const rows = params.targets.map((target) => ({
        ...shared,
        scope: target.scope,
        apartment_id: target.scope === "apartment" ? target.apartmentId : null,
        room_id: target.scope === "room" ? target.roomId : null,
        booking_id: target.scope === "tenant" ? target.bookingId : null,
      }));

      const { error } = await supabase.from("apartment_documents").insert(rows);
      if (error) {
        // Same rule as the single-target upload: never leave bytes behind that
        // no row references.
        await supabase.storage.from(DOC_BUCKET).remove([path]);
        throw error;
      }
    },
    onSuccess: (_data, vars) => {
      /* Every level that could now be stale, not just the ones picked: a
         property-level list and an apartment list can both be on screen. */
      invalidateDocumentQueries(qc, {
        propertyId: vars.propertyId,
        apartmentId: null,
        roomId: null,
      });
      for (const target of vars.targets) {
        if (target.scope === "apartment") {
          qc.invalidateQueries({
            queryKey: ["v2", "apartment-documents", target.apartmentId],
          });
        } else if (target.scope === "room") {
          qc.invalidateQueries({
            queryKey: ["v2", "room-documents", target.roomId],
          });
        } else if (target.scope === "tenant") {
          qc.invalidateQueries({
            queryKey: ["v2", "tenant-documents-filed", target.bookingId],
          });
        }
      }
      qc.invalidateQueries({
        queryKey: ["owner-property-document-history", vars.propertyId],
      });
    },
  });
}

/** Every list a single document can appear in. Missing one leaves a stale tab. */
function invalidateDocumentQueries(
  qc: ReturnType<typeof useQueryClient>,
  target: {
    propertyId: number | null;
    apartmentId: number | null;
    roomId: number | null;
    bookingId?: number | null;
  }
) {
  qc.invalidateQueries({ queryKey: ["v2", "property-apartment-documents"] });
  qc.invalidateQueries({
    queryKey: ["v2", "property-level-documents", target.propertyId],
  });
  if (target.apartmentId != null) {
    qc.invalidateQueries({
      queryKey: ["v2", "apartment-documents", target.apartmentId],
    });
  }
  if (target.roomId != null) {
    qc.invalidateQueries({ queryKey: ["v2", "room-documents", target.roomId] });
  }
  if (target.bookingId != null) {
    qc.invalidateQueries({
      queryKey: ["v2", "tenant-documents-filed", target.bookingId],
    });
  }
}

/** Edit the note on a document. RLS rejects it unless the row is yours (or you are staff). */
export function useUpdateDocumentMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      doc: ApartmentDocument;
      message: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from("apartment_documents")
        .update({ message: params.message.trim() || null })
        .eq("id", params.doc.id);
      if (error) throw error;
    },
    onSuccess: (_data, params) => {
      invalidateDocumentQueries(qc, {
        propertyId: params.doc.property_id,
        apartmentId: params.doc.apartment_id,
        roomId: params.doc.room_id,
        bookingId: params.doc.booking_id,
      });
    },
  });
}

/**
 * Admin-only hard delete — staff keep the FOR ALL RLS policy either way.
 *
 * The row goes; the bytes go only when nothing else points at them.
 * useUploadDocumentToTargets files one object under several rows, so removing
 * it for the first row deleted would break the file on every sibling card.
 * Soft-deleted siblings count too: the activity history still opens them.
 *
 * The check runs under the caller's RLS, which covers every sibling the app
 * creates: the app only shares a file between rows of one property (the upload
 * writes them in one insert), and whoever may delete a row on a property may
 * read that property's rows. It is not scoped to property_id, so staff also
 * see a stray reference from another property; an operator cannot see rows on
 * properties outside their access, and only a server-side check would. A hard
 * delete is rare enough to afford the scan (file_url has no index). If the
 * check itself fails the file stays — an orphan in the bucket costs bytes, a
 * missing file costs a document.
 */
export function useDeleteApartmentDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (doc: ApartmentDocument): Promise<void> => {
      const { error } = await supabase
        .from("apartment_documents")
        .delete()
        .eq("id", doc.id);
      if (error) throw error;
      if (!doc.file_url || /^https?:\/\//i.test(doc.file_url)) return;
      const { data: siblings, error: siblingError } = await supabase
        .from("apartment_documents")
        .select("id")
        .eq("file_url", doc.file_url)
        .limit(1);
      if (siblingError || (siblings ?? []).length > 0) return;
      await supabase.storage.from(DOC_BUCKET).remove([doc.file_url]);
    },
    onSuccess: (_data, doc) => {
      qc.invalidateQueries({
        queryKey: ["v2", "apartment-documents", doc.apartment_id],
      });
      qc.invalidateQueries({
        queryKey: ["v2", "property-apartment-documents", doc.property_id],
      });
    },
  });
}

/** What rename and soft-delete need: the row, plus the keys they invalidate.
 *  Narrower than ApartmentDocument so the owner Dokumente page can pass an
 *  owner_portfolio_documents row. */
type ApartmentDocumentRef = Pick<
  ApartmentDocument,
  "id" | "apartment_id" | "property_id"
>;

/** Owner renames their own upload. RLS rejects the update if it isn't theirs. */
export function useRenameApartmentDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      doc: ApartmentDocumentRef;
      title: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from("apartment_documents")
        .update({ title: params.title.trim() })
        .eq("id", params.doc.id);
      if (error) throw error;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({
        queryKey: ["v2", "apartment-documents", params.doc.apartment_id],
      });
      qc.invalidateQueries({
        queryKey: [
          "v2",
          "property-apartment-documents",
          params.doc.property_id,
        ],
      });
    },
  });
}

/** Owner removes their own upload — a reason is required and kept (soft
 *  delete: deleted_at + delete_reason), the file stays in storage. */
export function useSoftDeleteApartmentDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      doc: ApartmentDocumentRef;
      reason: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from("apartment_documents")
        .update({
          deleted_at: new Date().toISOString(),
          delete_reason: params.reason.trim(),
        })
        .eq("id", params.doc.id);
      if (error) throw error;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({
        queryKey: ["v2", "apartment-documents", params.doc.apartment_id],
      });
      qc.invalidateQueries({
        queryKey: [
          "v2",
          "property-apartment-documents",
          params.doc.property_id,
        ],
      });
    },
  });
}
